import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaCheckCircle, FaShieldAlt, FaUsers, FaChartLine, FaHandshake, FaAward, FaPhone, FaStar, FaBuilding, FaMobileAlt, FaBitcoin, FaShoppingCart, FaSeedling, FaShip, FaPlane, FaUniversity, FaWhatsapp } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import API_URL from '../config/api';
import ImageSlider from '../components/ImageSlider';

const Home = () => {
  const { t } = useTranslation();
  const [companyInfo, setCompanyInfo] = useState(null);

  useEffect(() => {
    fetchCompanyInfo();
  }, []);

  const fetchCompanyInfo = async () => {
    try {
      const response = await axios.get(`${API_URL}/company`);
      if (response.data.success) {
        setCompanyInfo(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching company info:', error);
    }
  };

  const services = [
    { 
      icon: <FaUniversity className="text-3xl" />, 
      title: t('services.page.bankInstrument.title'), 
      description: t('services.page.bankInstrument.description'),
    },
    { 
      icon: <FaChartLine className="text-3xl" />, 
      title: t('services.page.ventureCapital.title'), 
      description: t('services.page.ventureCapital.description'),
    },
    { 
      icon: <FaBuilding className="text-3xl" />, 
      title: t('services.page.holding.title'), 
      description: t('services.page.holding.description'),
    },
    { 
      icon: <FaMobileAlt className="text-3xl" />, 
      title: t('services.page.fintech.title'), 
      description: t('services.page.fintech.description'),
    },
    { 
      icon: <FaBitcoin className="text-3xl" />, 
      title: t('services.page.crypto.title'), 
      description: t('services.page.crypto.description'),
    },
    { 
      icon: <FaShoppingCart className="text-3xl" />, 
      title: t('services.page.ecommerce.title'), 
      description: t('services.page.ecommerce.description'),
    },
    { 
      icon: <FaSeedling className="text-3xl" />, 
      title: t('services.page.commodity.title'), 
      description: t('services.page.commodity.description'),
    },
    { 
      icon: <FaShip className="text-3xl" />, 
      title: t('services.page.trading.title'), 
      description: t('services.page.trading.description'),
    },
    { 
      icon: <FaPlane className="text-3xl" />, 
      title: t('services.page.travel.title'), 
      description: t('services.page.travel.description'),
    },
  ];

  const features = [
    t('about.features.consultation'),
    t('about.features.planning'),
    t('about.features.portfolio'),
    t('about.features.risk'),
    t('about.features.restructuring'),
    t('about.features.compliance'),
  ];

  return (
    <div className="overflow-x-hidden bg-white">
      {/* Hero Section - ELEGANT MIX */}
      <section className="relative min-h-screen flex items-center bg-gradient-to-br from-black-900 via-maroon-900 to-brown-900 overflow-hidden">
        {/* Subtle Animated Background - dari Original */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 -left-4 w-72 h-72 bg-gold-400 rounded-full mix-blend-screen filter blur-3xl animate-blob"></div>
          <div className="absolute top-0 -right-4 w-72 h-72 bg-maroon-500 rounded-full mix-blend-screen filter blur-3xl animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-20 w-72 h-72 bg-brown-600 rounded-full mix-blend-screen filter blur-3xl animate-blob animation-delay-4000"></div>
        </div>

        {/* Subtle Pattern Overlay */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>

        {/* Decorative Floating Dots - subtle */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-2 h-2 bg-gold-400 rounded-full animate-pulse opacity-50"></div>
          <div className="absolute top-40 right-20 w-2 h-2 bg-gold-300 rounded-full animate-ping opacity-40"></div>
          <div className="absolute bottom-32 left-1/4 w-2 h-2 bg-maroon-400 rounded-full animate-bounce opacity-40"></div>
        </div>

        <div className="container mx-auto px-6 lg:px-16 xl:px-24 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div className="text-white">
              {/* Badge - dengan animation */}
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-gold-500/20 border border-gold-500/30 mb-8 backdrop-blur-sm animate-fade-in">
                <FaStar className="text-gold-400 mr-2 animate-pulse" />
                <span className="text-gold-200 font-semibold text-sm">Sustainable • Global • Trust</span>
              </div>

              {/* Main Heading - dengan animation */}
              <h1 className="text-5xl lg:text-6xl xl:text-7xl font-bold mb-6 leading-tight animate-slide-up">
                <span className="text-white">{t('hero.title1')}</span>
                <br />
                <span className="bg-gradient-to-r from-gold-300 via-gold-400 to-gold-500 bg-clip-text text-transparent">
                  {t('hero.title2')}
                </span>
              </h1>

              {/* Subtitle - dengan animation */}
              <p className="text-xl text-gray-300 mb-10 leading-relaxed max-w-xl animate-fade-in">
                {t('hero.subtitle')}
              </p>

              {/* CTA Buttons - enhanced hover */}
              <div className="flex flex-wrap gap-4 animate-slide-up">
                <Link 
                  to="/contact" 
                  className="group relative px-8 py-4 bg-gradient-to-r from-gold-500 to-gold-600 text-black-900 font-bold rounded-lg hover:from-gold-400 hover:to-gold-500 transition-all duration-300 hover:shadow-2xl hover:shadow-gold-500/50 hover:scale-105 flex items-center overflow-hidden"
                >
                  <span className="relative z-10 flex items-center">
                    {t('common.getStarted')}
                    <FaArrowRight className="ml-3 group-hover:translate-x-2 transition-transform" />
                  </span>
                </Link>
                
                <Link 
                  to="/about" 
                  className="group px-8 py-4 bg-transparent text-white font-bold rounded-lg border-2 border-white/30 hover:bg-white hover:text-black-900 transition-all duration-300 hover:scale-105 flex items-center"
                >
                  {t('common.learnMore')}
                  <FaArrowRight className="ml-3 group-hover:translate-x-2 transition-transform" />
                </Link>
              </div>

              {/* Contact Info */}
              <div className="mt-10 pt-10 border-t border-white/20">
                <div className="inline-flex items-center gap-4 px-6 py-4 bg-white/10 backdrop-blur-md rounded-xl border border-white/20 hover:bg-white/15 transition-all duration-300">
                  <div className="flex-shrink-0 p-3 bg-green-500 rounded-lg">
                    <FaWhatsapp className="text-white text-xl" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-300">{t('common.needConsultation')}</p>
                    <a 
                      href="https://wa.me/447423369031"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-lg font-bold text-white whitespace-nowrap hover:text-gold-400 transition-colors"
                    >
                      +44-7423369031
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Content - Stats Cards dengan subtle animation */}
            <div className="grid grid-cols-2 gap-6">
              {/* Card 1 */}
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 hover:bg-white/15 hover:border-gold-400/50 transition-all duration-300 hover:scale-105 animate-fade-in">
                <FaUsers className="text-5xl text-gold-400 mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-4xl font-bold text-white mb-2">20+</h3>
                <p className="text-gray-300 font-medium">{t('stats.clients')}</p>
              </div>

              {/* Card 2 */}
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 hover:bg-white/15 hover:border-gold-400/50 transition-all duration-300 hover:scale-105 animate-fade-in animation-delay-200">
                <FaAward className="text-5xl text-gold-400 mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-4xl font-bold text-white mb-2">15+</h3>
                <p className="text-gray-300 font-medium">{t('stats.yearsExcellence')}</p>
              </div>

              {/* Card 3 */}
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 hover:bg-white/15 hover:border-gold-400/50 transition-all duration-300 hover:scale-105 animate-fade-in animation-delay-400">
                <FaChartLine className="text-5xl text-gold-400 mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-4xl font-bold text-white mb-2">90%</h3>
                <p className="text-gray-300 font-medium">{t('stats.successRate')}</p>
              </div>

              {/* Card 4 */}
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 hover:bg-white/15 hover:border-gold-400/50 transition-all duration-300 hover:scale-105 animate-fade-in animation-delay-600">
                <FaHandshake className="text-5xl text-gold-400 mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-4xl font-bold text-white mb-2">20+</h3>
                <p className="text-gray-300 font-medium">{t('stats.projectsDone')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" className="w-full h-auto">
            <path fill="#ffffff" d="M0,64L48,69.3C96,75,192,85,288,80C384,75,480,53,576,48C672,43,768,53,864,58.7C960,64,1056,64,1152,58.7C1248,53,1344,43,1392,37.3L1440,32L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z"></path>
          </svg>
        </div>
      </section>

      {/* About Section - SIMPLE & CLEAN */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6 lg:px-16 xl:px-24">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div>
              <div className="inline-block px-4 py-2 bg-maroon-100 rounded-lg mb-6">
                <span className="text-maroon-700 font-bold text-sm uppercase tracking-wider">{t('about.sectionTitle')}</span>
              </div>
              
              <h2 className="text-4xl lg:text-5xl font-bold text-black-900 mb-6 leading-tight">
                {t('about.title')}
                <span className="text-maroon-700"> {t('about.titleHighlight')}</span>
              </h2>
              
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                {t('about.description')}
              </p>

              {/* Features List */}
              <div className="space-y-4 mb-8">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center group">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gold-500 flex items-center justify-center mr-4 group-hover:scale-110 transition-transform">
                      <FaCheckCircle className="text-white text-xs" />
                    </div>
                    <span className="text-gray-700 font-medium group-hover:text-maroon-700 transition-colors">{feature}</span>
                  </div>
                ))}
              </div>

              <Link 
                to="/about" 
                className="inline-flex items-center px-8 py-4 bg-maroon-700 text-white font-bold rounded-lg hover:bg-maroon-800 transition-all duration-300 hover:shadow-xl group"
              >
                {t('common.discoverMore')}
                <FaArrowRight className="ml-3 group-hover:translate-x-2 transition-transform" />
              </Link>
            </div>

            {/* Right Image/Decoration */}
            <div className="relative">
              <div className="absolute -top-8 -left-8 w-64 h-64 bg-gold-100 rounded-full opacity-30 blur-3xl"></div>
              <div className="absolute -bottom-8 -right-8 w-64 h-64 bg-maroon-100 rounded-full opacity-30 blur-3xl"></div>
              
              <div className="relative bg-gradient-to-br from-maroon-50 to-brown-50 rounded-3xl p-12 border-2 border-gold-200">
                <div className="space-y-6">
                  <div className="flex items-center gap-4 p-6 bg-white rounded-2xl shadow-lg">
                    <div className="w-12 h-12 bg-maroon-700 rounded-full flex items-center justify-center">
                      <FaShieldAlt className="text-white text-xl" />
                    </div>
                    <div>
                      <p className="font-bold text-black-900">{t('about.highlights.security.title')}</p>
                      <p className="text-sm text-gray-600">{t('about.highlights.security.description')}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 p-6 bg-white rounded-2xl shadow-lg">
                    <div className="w-12 h-12 bg-gold-500 rounded-full flex items-center justify-center">
                      <FaChartLine className="text-white text-xl" />
                    </div>
                    <div>
                      <p className="font-bold text-black-900">{t('about.highlights.growth.title')}</p>
                      <p className="text-sm text-gray-600">{t('about.highlights.growth.description')}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 p-6 bg-white rounded-2xl shadow-lg">
                    <div className="w-12 h-12 bg-brown-600 rounded-full flex items-center justify-center">
                      <FaHandshake className="text-white text-xl" />
                    </div>
                    <div>
                      <p className="font-bold text-black-900">{t('about.highlights.team.title')}</p>
                      <p className="text-sm text-gray-600">{t('about.highlights.team.description')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Image Slider Section - ELEGANT SHOWCASE */}
      <section className="py-24 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden">
        {/* Background Decoration */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-maroon-100 to-transparent rounded-full filter blur-3xl opacity-20"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-br from-gold-100 to-transparent rounded-full filter blur-3xl opacity-20"></div>

        <div className="container mx-auto px-6 lg:px-16 xl:px-24 relative z-10">
          {/* Section Header */}
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-block px-4 py-2 bg-gold-100 rounded-lg mb-6">
              <span className="text-maroon-700 font-bold text-sm uppercase tracking-wider">{t('gallery.sectionTitle')}</span>
            </div>
            
            <h2 className="text-4xl lg:text-5xl font-bold text-black-900 mb-6 leading-tight">
              <span className="text-black-900">{t('gallery.title')} </span>
              <span className="bg-gradient-to-r from-maroon-700 to-gold-600 bg-clip-text text-transparent">
                {t('gallery.titleHighlight')}
              </span>
            </h2>
            
            <p className="text-xl text-gray-600 leading-relaxed">
              {t('gallery.subtitle')}
            </p>
          </div>

          {/* Slider Container - Proportional & Elegant */}
          <div className="max-w-6xl mx-auto">
            <div className="relative aspect-video rounded-3xl overflow-hidden shadow-2xl shadow-maroon-900/20 border-4 border-white">
              <ImageSlider />
            </div>
          </div>

          {/* Optional: Stats below slider */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto mt-16">
            <div className="text-center p-6 bg-white rounded-xl shadow-lg border border-gray-100 hover:shadow-xl hover:scale-105 transition-all duration-300">
              <div className="text-3xl font-bold text-maroon-700 mb-2">20+</div>
              <p className="text-sm text-gray-600 font-medium">{t('gallery.stats.projects')}</p>
            </div>
            <div className="text-center p-6 bg-white rounded-xl shadow-lg border border-gray-100 hover:shadow-xl hover:scale-105 transition-all duration-300">
              <div className="text-3xl font-bold text-gold-600 mb-2">15+</div>
              <p className="text-sm text-gray-600 font-medium">{t('gallery.stats.years')}</p>
            </div>
            <div className="text-center p-6 bg-white rounded-xl shadow-lg border border-gray-100 hover:shadow-xl hover:scale-105 transition-all duration-300">
              <div className="text-3xl font-bold text-maroon-700 mb-2">90%</div>
              <p className="text-sm text-gray-600 font-medium">{t('gallery.stats.success')}</p>
            </div>
            <div className="text-center p-6 bg-white rounded-xl shadow-lg border border-gray-100 hover:shadow-xl hover:scale-105 transition-all duration-300">
              <div className="text-3xl font-bold text-gold-600 mb-2">24/7</div>
              <p className="text-sm text-gray-600 font-medium">{t('gallery.stats.support')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section - ELEGANT CARDS */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-6 lg:px-16 xl:px-24">
          {/* Section Header */}
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-block px-4 py-2 bg-maroon-100 rounded-lg mb-6">
              <span className="text-maroon-700 font-bold text-sm uppercase tracking-wider">{t('services.sectionTitle')}</span>
            </div>
            
            <h2 className="text-4xl lg:text-5xl font-bold text-black-900 mb-6 leading-tight">
              {t('services.title')}
            </h2>
            
            <p className="text-xl text-gray-600 leading-relaxed">
              {t('services.subtitle')}
            </p>
          </div>

          {/* Services Grid - dengan subtle animation */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <div 
                key={index} 
                className="group bg-white rounded-2xl p-8 border-2 border-gray-100 hover:border-gold-400 transition-all duration-300 hover:shadow-2xl hover:shadow-gold-500/20 hover:-translate-y-2 animate-fade-in"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-16 h-16 bg-gradient-to-br from-maroon-700 to-maroon-900 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:shadow-lg group-hover:shadow-maroon-500/50 transition-all duration-300 text-white">
                  {service.icon}
                </div>
                
                <h3 className="text-xl font-bold text-black-900 mb-4 group-hover:text-maroon-700 transition-colors">
                  {service.title}
                </h3>
                
                <p className="text-gray-600 leading-relaxed mb-6">
                  {service.description}
                </p>

                <Link 
                  to="/services" 
                  className="inline-flex items-center text-maroon-700 font-semibold group-hover:text-gold-600 transition-colors"
                >
                  {t('common.learnMore')}
                  <FaArrowRight className="ml-2 group-hover:translate-x-2 transition-transform" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - CLEAN & POWERFUL */}
      <section className="relative py-24 bg-gradient-to-br from-maroon-900 via-black-900 to-brown-900 text-white overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-gold-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-maroon-500/10 rounded-full blur-3xl"></div>

        <div className="container mx-auto px-6 lg:px-16 xl:px-24 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl lg:text-5xl font-bold mb-6 leading-tight">
              {t('cta.title')}
              <span className="text-gold-400"> {t('cta.titleHighlight')}</span>
            </h2>

            <p className="text-xl text-gray-300 mb-12 leading-relaxed max-w-2xl mx-auto">
              {t('cta.subtitle')}
            </p>

            <div className="flex flex-wrap justify-center gap-6">
              <Link 
                to="/contact" 
                className="group px-10 py-5 bg-gold-500 text-black-900 font-bold text-lg rounded-lg hover:bg-gold-400 transition-all duration-300 hover:shadow-2xl hover:shadow-gold-500/50 inline-flex items-center"
              >
                {t('common.scheduleConsultation')}
                <FaArrowRight className="ml-3 group-hover:translate-x-2 transition-transform" />
              </Link>

              <Link 
                to="/services" 
                className="group px-10 py-5 bg-transparent text-white font-bold text-lg rounded-lg border-2 border-white/30 hover:bg-white hover:text-black-900 transition-all duration-300 inline-flex items-center"
              >
                {t('common.viewAllServices')}
                <FaArrowRight className="ml-3 group-hover:translate-x-2 transition-transform" />
              </Link>
            </div>

            {/* Trust Indicators */}
            <div className="grid grid-cols-3 gap-8 mt-16 pt-16 border-t border-white/20">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-gold-500/20 rounded-full mb-4">
                  <FaShieldAlt className="text-gold-400 text-xl" />
                </div>
                <p className="text-sm text-gray-300">{t('cta.trustIndicators.certified')}</p>
              </div>
              
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-gold-500/20 rounded-full mb-4">
                  <FaAward className="text-gold-400 text-xl" />
                </div>
                <p className="text-sm text-gray-300">{t('cta.trustIndicators.award')}</p>
              </div>
              
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-gold-500/20 rounded-full mb-4">
                  <FaStar className="text-gold-400 text-xl" />
                </div>
                <p className="text-sm text-gray-300">{t('cta.trustIndicators.rated')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
